import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const SinglePlatformAnimation = ({
  isAnimating,
  onAnimationComplete,
  platform = 'linkedin',
  duration = 2500,
  status = null // 'success', 'error', or null (still in progress)
}) => {
  const [animationState, setAnimationState] = useState('idle'); // idle, transferring, complete, failed

  useEffect(() => {
    if (isAnimating && !status) {
      // Start the transferring animation
      setAnimationState('transferring');
    } else if (status === 'success') {
      setAnimationState('complete');
      const timer = setTimeout(() => {
        onAnimationComplete?.();
      }, 1200);
      return () => clearTimeout(timer);
    } else if (status === 'error') {
      setAnimationState('failed');
      const timer = setTimeout(() => {
        onAnimationComplete?.();
      }, 1500);
      return () => clearTimeout(timer);
    } else if (!isAnimating) {
      setAnimationState('idle');
    }
  }, [isAnimating, status, onAnimationComplete]);

  if (!isAnimating && animationState === 'idle') {
    return null;
  }

  const platformConfig = {
    linkedin: {
      icon: 'Linkedin',
      color: '#0A66C2',
      name: 'LinkedIn'
    },
    twitter: {
      icon: 'Twitter',
      color: '#000000',
      name: 'X'
    }
  };

  const config = platformConfig[platform] || platformConfig.linkedin;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-card rounded-2xl p-8 max-w-md w-full mx-4 border border-border shadow-2xl">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {animationState === 'failed' ? 'Publishing Failed' : `Publishing to ${config.name}`}
          </h3>
          <p className="text-sm text-muted-foreground">
            {animationState === 'transferring' && `Sending your content to ${config.name}...`}
            {animationState === 'complete' && 'Done!'}
            {animationState === 'failed' && 'Could not publish your content.'}
          </p>
        </div>

        {/* Animation Container */}
        <div className="relative h-24 flex items-center justify-between px-4">
          {/* Source Platform */}
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-2 shadow-lg">
              <Icon name="FileText" size={20} color="white" />
            </div>
            <span className="text-xs text-muted-foreground font-medium">Our Platform</span>
          </div>

          {/* Animated Content Transfer */}
          <div className="flex-1 relative mx-6">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-border rounded-full transform -translate-y-1/2">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-in-out"
                style={{
                  background: animationState === 'failed'
                    ? 'var(--color-destructive, #ef4444)'
                    : `linear-gradient(to right, var(--color-primary), ${config.color})`,
                  width: animationState === 'idle' ? '0%' : '100%'
                }}
              />
            </div>

            {animationState === 'transferring' && (
              <>
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute top-1/2 w-2 h-2 rounded-full transform -translate-y-1/2 animate-ping"
                    style={{
                      backgroundColor: config.color,
                      left: `${30 + (i * 25)}%`,
                      animationDelay: `${i * 400}ms`,
                      animationDuration: '0.8s'
                    }}
                  />
                ))}
              </>
            )}

            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              {animationState === 'failed' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-destructive, #ef4444)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              ) : (
                <Icon
                  name="ArrowRight"
                  size={16}
                  color={animationState === 'transferring' ? config.color : 'var(--color-muted-foreground)'}
                  className={`transition-all duration-300 ${animationState === 'transferring' ? 'animate-pulse' : ''}`}
                />
              )}
            </div>
          </div>

          {/* Destination Platform */}
          <div className="flex flex-col items-center">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-md transition-all duration-500 ${
                animationState === 'complete' ? 'scale-110 ring-2 ring-opacity-50'
                : animationState === 'failed' ? 'opacity-50'
                : animationState === 'transferring' ? 'scale-105'
                : 'scale-100'
              }`}
              style={{
                backgroundColor: animationState === 'failed' ? 'var(--color-muted)' : config.color,
                ringColor: config.color
              }}
            >
              <Icon name={config.icon} size={18} color="white" />
            </div>
            <span className="text-xs text-muted-foreground font-medium mt-1">{config.name}</span>
          </div>
        </div>

        {/* Status Text */}
        <div className="text-center mt-6">
          <p className="text-sm">
            {animationState === 'transferring' && (
              <span className="text-muted-foreground">Publishing to {config.name}...</span>
            )}
            {animationState === 'complete' && (
              <span className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                <Icon name="CheckCircle" size={16} />
                Published to {config.name} successfully!
              </span>
            )}
            {animationState === 'failed' && (
              <span className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                <Icon name="AlertCircle" size={16} />
                Failed to publish. Check the error below.
              </span>
            )}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mt-4">
          <div className="w-full bg-border rounded-full h-1">
            <div
              className="h-1 rounded-full transition-all duration-1000 ease-out"
              style={{
                backgroundColor: animationState === 'failed' ? 'var(--color-destructive, #ef4444)' : config.color,
                width: animationState === 'idle' ? '0%' : '100%'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SinglePlatformAnimation;
